import PackageDetailsForm from "@stories/molecules/PackageDetailsForm/PackageDetailsForm";
import { Steps } from "../../atoms/Steps/Steps";
import {
  ProfileStatus,
  ProfileType,
  createDefaultProfile,
} from "@interfaces/profile";
import { Dialog, DialogContent, DialogTitle, Stack } from "@mui/material";
import {
  ContractorPackageLineItem,
  createDefaultContractorPackage,
} from "@interfaces/contractorPackage";
import { useEffect, useState } from "react";
import { SocialRequirementForm } from "@stories/molecules/SocialRequirementForm/SocialRequirementForm";
import { ProjectLineItem } from "@interfaces/project";
import {
  SocialCommitmentLineItem,
  cloneCommitments,
} from "@interfaces/commitment";
import { useProfile } from "@hooks/crud/useProfile";
import { PackageMPSGForm } from "@stories/molecules/PackageMPSGForm/PackageMPSGForm";
import { OrganisationLineItem } from "@interfaces/organisation";
import { useOrganisation } from "@hooks/crud/useOrganisation";

interface ContractorPackageModalProps {
  open: boolean;
  loading: boolean;
  editMode: boolean;
  project: ProjectLineItem;
  existingContractorPackage: ContractorPackageLineItem;
  createContractorPackage: (
    contractorPackage: ContractorPackageLineItem
  ) => void;
  updateContractorPackage: (
    contractorPackage: ContractorPackageLineItem
  ) => void;
  handleClose: () => void;
}

export default function ContractorPackageModal({
  open,
  loading,
  editMode,
  project,
  existingContractorPackage,
  createContractorPackage,
  updateContractorPackage,
  handleClose,
}: ContractorPackageModalProps) {
  const { createOrganisation } = useOrganisation();
  const { createProfile } = useProfile();

  const [activeStep, setActiveStep] = useState(0);

  const [selectedContractorPackage, setSelectedContractorPackage] = useState(
    existingContractorPackage
  );

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  const [selectedContractor, setSelectedContractor] = useState<
    OrganisationLineItem | undefined
  >(undefined);

  useEffect(() => {
    if (existingContractorPackage) {
      setSelectedContractorPackage(existingContractorPackage);
    } else {
      setSelectedContractorPackage(createDefaultContractorPackage(project));
    }
  }, [existingContractorPackage]);

  const onSubmit = async (commitments: SocialCommitmentLineItem[]) => {
    if (selectedContractorPackage) {
      const contractorPackageToSave: ContractorPackageLineItem = {
        ...selectedContractorPackage,
        commitments,
      };
      if (editMode) {
        await updateContractorPackage(contractorPackageToSave);
      } else {
        if (selectedContractor) {
          if (selectedContractor.users.length === 0) {
            const newOrganisation = await createOrganisation(
              selectedContractor
            );
            const newProfile = await createProfile({
              ...createDefaultProfile(newOrganisation, {
                email: newOrganisation.representativeEmail,
                id: newOrganisation.id,
              }),
              status: ProfileStatus.Invited,
              type: ProfileType.Admin,
            });
            newOrganisation.users = [newProfile];
            contractorPackageToSave.contractorId = newOrganisation.id;
            contractorPackageToSave.contractorName =
              newOrganisation.organisationName;
            contractorPackageToSave.contractorLogo =
              newOrganisation.organisationLogo;
          }
        }

        await createContractorPackage({
          ...contractorPackageToSave,
          sendInvite: true,
        });
      }
      handleClose();
    }
    throw Error("No package details");
  };
  const title = existingContractorPackage ? "Edit Package" : "Create Package";

  const selectedCreateContractorStep = (() => {
    switch (activeStep) {
      default:
        return null;
      case 0:
        return (
          <PackageDetailsForm
            project={project}
            contractorPackage={selectedContractorPackage}
            handleSubmit={(
              contractorPackage: ContractorPackageLineItem,
              contractorOrganisation: OrganisationLineItem
            ) => {
              setSelectedContractorPackage({
                ...contractorPackage,
              });
              setSelectedContractor(contractorOrganisation);
              handleNext();
            }}
          />
        );
      case 1:
        return (
          <PackageMPSGForm
            contractorPackage={selectedContractorPackage}
            handleBack={handleBack}
            handleSubmit={(contractorPackage: ContractorPackageLineItem) => {
              setSelectedContractorPackage(contractorPackage);
              handleNext();
            }}
          />
        );
      case 2:
        return (
          <SocialRequirementForm
            loading={loading}
            handleBack={handleBack}
            project={project}
            commitmentLineItems={
              selectedContractorPackage?.commitments ||
              cloneCommitments(project.commitments)
            }
            onSubmit={onSubmit}
          />
        );
    }
  })();

  const steps = ["Package Details", "Configure", "Social Procurement"];

  const onClose = () => {
    setSelectedContractor(undefined);
    setActiveStep(0);
    handleClose();
  };
  return (
    <div>
      <Dialog fullWidth={true} open={open} onClose={onClose} maxWidth={"lg"}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Steps activeStep={activeStep} steps={steps} />

            {selectedCreateContractorStep}
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
}
